import React, { ReactElement, useEffect, useState } from 'react';
import { FileTextIcon } from 'lucide-react';
import { useAppStateContext } from 'src/AppState/AppState';
import { PopupsStackState } from 'src/AppState/PopupsStackState';
import { ToasterState } from 'src/AppState/ToasterState';
import { useCommon } from 'src/AppState/Common';
import { LoadingV2 } from 'src/components/Loading/LoadingV2';
import { observer } from 'src/utils/mobx-react';
import { MarkersOfHarmPopupForm } from './MarkersOfHarmPopupForm';
import { MarkersOfHarmPopupFormState } from './MarkersOfHarmPopupForm.state';
import { MarkersOfHarmPopupListItem } from './MarkersOfHarmPopupListItem';
import { MarkersOfHarmPopupPreview } from './MarkersOfHarmPopupPreview';
import { hydrateAppliedActionSettingsFromAutomation } from './MarkersOfHarmPopupAutomation.mapper';
import { MarkersOfHarmPopupTabState } from './MarkersOfHarmPopupTab.state';
import {
    MARKERS_OF_HARM_POPUP_FILTER_OPTIONS,
    MarkersOfHarmPopupPreviewContentType,
    MarkersOfHarmPopupType,
} from './MarkersOfHarmPopupTab.types';
import { MarkersOfHarmPopupSortMenu } from './MarkersOfHarmPopupSortMenu';
import { TabList, TabTrigger } from 'src/domains/common/components/tabs/tabs';

const mapPopupToPreviewContent = (popup: MarkersOfHarmPopupType): MarkersOfHarmPopupPreviewContentType => ({
    title: popup.title,
    bodyText: popup.bodyText,
    primaryButton: {
        text: popup.primaryButton.text,
    },
    secondaryButton:
        popup.secondaryButton === undefined
            ? undefined
            : {
                  text: popup.secondaryButton.text,
              },
});

export const MarkersOfHarmPopupTab = observer('MarkersOfHarmPopupTab', (): ReactElement => {
    const common = useCommon();
    const { trpcClient } = useAppStateContext();
    const [tabState] = useState(() => new MarkersOfHarmPopupTabState(trpcClient));
    const [formState] = useState(() => new MarkersOfHarmPopupFormState());
    const [previewContent, setPreviewContent] = useState<MarkersOfHarmPopupPreviewContentType | null>(null);

    useEffect(() => {
        if (tabState.errorMessage === null) {
            return;
        }

        ToasterState.get(common).setToast({
            variant: 'error',
            title: 'Could not save Markers Of Harm Pop-Up.',
            message: tabState.errorMessage,
        });
        tabState.setError(null);
    }, [common, tabState, tabState.errorMessage]);

    useEffect(() => {
        formState.setTriggerTemplates(tabState.triggerTemplates);
    }, [formState, tabState.triggerTemplates]);

    const loadPopupIntoForm = async (popup: MarkersOfHarmPopupType): Promise<void> => {
        const popupWithTrigger = await tabState.getPopupWithTriggerFromMoh(popup);
        formState.loadFromPopup(popupWithTrigger);

        const automationName = tabState.getPopupAutomationName(popupWithTrigger);
        const automation = automationName === null ? null : await tabState.getComplianceAutomation(automationName);
        if (automation === null) {
            formState.resetAppliedActionFields();
        } else {
            formState.loadAppliedActionSettings(hydrateAppliedActionSettingsFromAutomation(automation));
        }

        formState.markCurrentStateAsSaved();
    };

    const handleSubmit = async (): Promise<void> => {
        const saved = await tabState.savePopupFromForm(formState);

        if (!saved) {
            if (tabState.errorMessage === null) {
                ToasterState.get(common).setToast({
                    variant: 'error',
                    title: 'Could not save Markers Of Harm Pop-Up.',
                    message: 'Please check the form and fix any errors before saving.',
                });
            }
            return;
        }

        formState.resetForm();
        tabState.clearSelectedPopup();
    };

    const handleEdit = (popup: MarkersOfHarmPopupType): void => {
        void (async (): Promise<void> => {
            await loadPopupIntoForm(popup);
            tabState.selectPopupForEdit(popup.id);
        })();
    };

    const discardCloseForm = (): void => {
        formState.resetForm();
        tabState.clearSelectedPopup();
    };

    const handleCloseForm = (): void => {
        if (!formState.hasUnsavedChanges) {
            discardCloseForm();
            return;
        }

        const popupsStack = PopupsStackState.get(common);
        const isEditMode = formState.isEditMode;

        popupsStack.addPopup('markers-of-harm-popup-discard-changes', {
            template: 'generic',
            title: isEditMode ? 'Discard changes' : 'Discard Pop-Up',
            message: (
                <p className='m-0'>
                    {isEditMode
                        ? 'Are you sure you want to discard your changes? Any unsaved edits will be lost.'
                        : 'Are you sure you want to discard this Pop-Up? Any unsaved changes will be lost.'}
                </p>
            ),
            hasOverlay: true,
            cancel_label: 'Cancel',
            confirm_label: 'Discard',
            onConfirm: () => {
                discardCloseForm();
                popupsStack.closePopupOnTop();
            },
            onCancel: () => {
                popupsStack.closePopupOnTop();
            },
        });
    };

    const handleFormPreview = (): void => {
        const content = formState.buildPreviewContent();
        if (content === null) {
            return;
        }

        setPreviewContent(content);
    };

    const handleShow = (popup: MarkersOfHarmPopupType): void => {
        setPreviewContent(mapPopupToPreviewContent(popup));
    };

    const handleClosePreview = (): void => {
        setPreviewContent(null);
    };

    const handleToggleActive = (popup: MarkersOfHarmPopupType, active: boolean): void => {
        void (async (): Promise<void> => {
            const updated = await tabState.setPopupActive(popup, active);
            if (!updated) {
                return;
            }

            if (formState.editingPopupId === popup.id) {
                formState.editingActive = active;
                formState.markCurrentStateAsSaved();
            }
        })();
    };

    if (tabState.isLoading) {
        return (
            <LoadingV2
                dataTest='markers-of-harm-popup-tab-loading'
                title='Loading...'
            />
        );
    }

    return (
        <div
            className='flex flex-auto gap-3 bg-muted p-6 text-foreground font-tertiary'
            data-test='markers-of-harm-popup-tab'
        >
            <section
                className='flex flex-col flex-[0_0_40%] min-w-0 gap-6 rounded-md bg-white p-6'
                data-test='markers-of-harm-popup-list-section'
            >
                <header className='flex flex-col gap-2 shrink-0'>
                    <h1
                        className='m-0 text-[24px] font-semibold leading-[32px]'
                        data-test='markers-of-harm-popup-page-title'
                    >
                        Markers Of Harm Pop-Up
                    </h1>
                    <p className='m-0 text-sm text-muted-foreground'>
                        Create and manage Pop-Ups displayed on your site.
                    </p>
                </header>

                <div
                    className='flex items-center justify-between gap-4 shrink-0'
                    data-test='markers-of-harm-popup-controls'
                >
                    <TabList dataTest='markers-of-harm-popup-filters'>
                        {MARKERS_OF_HARM_POPUP_FILTER_OPTIONS.map((filterOption) => (
                            <TabTrigger
                                key={filterOption.id}
                                active={tabState.activeFilter === filterOption.id}
                                dataTest={`markers-of-harm-popup-filter-${filterOption.id}`}
                                onClick={(): void => tabState.setActiveFilter(filterOption.id)}
                            >
                                {filterOption.label}
                            </TabTrigger>
                        ))}
                    </TabList>

                    <MarkersOfHarmPopupSortMenu
                        sortBy={tabState.sortBy}
                        onSortChange={tabState.setSortBy}
                    />
                </div>

                <div
                    className='flex flex-col flex-auto min-h-0 overflow-y-auto rounded-md border border-dashed border-border p-6'
                    data-test='markers-of-harm-popup-list'
                >
                    {tabState.filteredAndSortedPopups.length === 0 ? (
                        <div
                            className='flex flex-col items-center justify-start gap-2 text-center'
                            data-test='markers-of-harm-popup-empty-state'
                        >
                            <FileTextIcon className='size-10 text-muted-foreground' />
                            <p className='m-0 text-base font-semibold text-foreground'>No Pop-Ups</p>
                            <p className='m-0 max-w-xs text-sm text-muted-foreground'>
                                {tabState.popups.length === 0
                                    ? 'You have not created any Pop-Ups for your website yet.'
                                    : 'No Pop-Ups match the selected filter.'}
                            </p>
                        </div>
                    ) : (
                        <ul className='m-0 flex flex-col gap-2 list-none p-0'>
                            {tabState.filteredAndSortedPopups.map((popup) => (
                                <MarkersOfHarmPopupListItem
                                    key={popup.id}
                                    popup={popup}
                                    isSelected={tabState.selectedPopupId === popup.id}
                                    isToggling={tabState.isSaving}
                                    onEdit={handleEdit}
                                    onShow={handleShow}
                                    onToggleActive={handleToggleActive}
                                />
                            ))}
                        </ul>
                    )}
                </div>
            </section>

            <section
                className='flex flex-col flex-1 min-w-0 rounded-md bg-white p-6'
                data-test='markers-of-harm-popup-form-section'
            >
                <MarkersOfHarmPopupForm
                    formState={formState}
                    isSubmitting={tabState.isSaving}
                    onSubmit={(): void => {
                        void handleSubmit();
                    }}
                    onPreview={handleFormPreview}
                    onClose={handleCloseForm}
                />
            </section>

            {previewContent === null ? null : (
                <MarkersOfHarmPopupPreview
                    content={previewContent}
                    onClose={handleClosePreview}
                />
            )}
        </div>
    );
});
